import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import type { ChartConfiguration, ChartDataset } from "chart.js";
import { groupBy, sum } from "../../services/stats.util";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";

// UI (header + filters)
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { ArticleFacadeService } from "../../services/article/article-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

type Mode = "CA" | "VOLUME";
type ViewType = "PIE" | "LIST";
type SortBy = "CA" | "VOLUME" | "ALPHA";

@Component({
  selector: "vl-achats-par-fournisseur",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    ChipsToggleComponent,
    SharedModule,
    ChipsComponent,
    PopoverModule,
  ],
  templateUrl: "./achats-par-fournisseur.component.html",
  styleUrls: ["./achats-par-fournisseur.component.scss"],
  host: { class: "widget-component" },
})
export class AchatsParFournisseurComponent {
  articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  // Canvas optionnel (évite NG0951 quand le canvas n’est pas rendu)
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("achatChart");
  chart?: Chart<"doughnut", number[], string>;

  // Légende externe
  legendLabels = signal<
    { label: string; percentage: string; color: string }[] | null
  >(null);

  // Toggles
  viewMode = signal<Mode>("CA");
  viewType = signal<ViewType>("PIE");
  sortBy = signal<SortBy>("CA");

  elementsMode = [
    { name: "CA", value: "CA" },
    { name: "Volume", value: "VOLUME" },
  ];
  elementsView = [
    { icon: "pie_chart", value: "PIE" },
    { icon: "list", value: "LIST" },
  ];
  elementsSort = [
    { name: "Tri CA", value: "CA" },
    { name: "Tri Volume", value: "VOLUME" },
    { name: "Tri Alpha", value: "ALPHA" },
  ];

  // Filtres
  topCount = signal<number>(12);
  search = signal<string>("");

  // Formatters
  private fmtEUR = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });
  private fmtINT = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

  // Agrégations par fournisseur
  private baseRows = computed(() => {
    const recs = (this.stats()?.receptions ?? []) as any[];
    if (!recs?.length) return [];

    const g = groupBy(
      recs,
      (r: any) => r?.commandeLigne?.commande?.tiersId ?? "Inconnu",
    );

    return Object.entries(g).map(([tiersId, es]) => {
      const label =
        (es[0] as any)?.commandeLigne?.commande?.tiers?.denomination ??
        String(tiersId);

      const qty = sum(
        es.map((e: any) => {
          const coef = parseNumber(
            e?.commandeLigne?.coefficientUniteGestion || 1,
          );
          const q = parseNumber(e?.quantite ?? e?.qty ?? 0);
          return q / (coef || 1);
        }),
      );

      const amount = es.reduce((a: number, e: any) => {
        const coef = parseNumber(
          e?.commandeLigne?.coefficientUniteGestion || 1,
        );
        const q = parseNumber(e?.quantite ?? e?.qty ?? 0) / (coef || 1);
        const pu = parseNumber(e?.prixUnitaire ?? 0);
        return a + q * pu;
      }, 0);

      return { key: tiersId, label, qty, amount };
    });
  });

  // Recherche
  private filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.baseRows();
    return this.baseRows().filter((r) => r.label?.toLowerCase?.().includes(q));
  });

  // Tri
  private sorted = computed(() => {
    const by = this.sortBy();
    const arr = [...this.filtered()];
    arr.sort((a, b) => {
      switch (by) {
        case "VOLUME":
          return b.qty - a.qty;
        case "ALPHA":
          return a.label.localeCompare(b.label, "fr");
        case "CA":
        default:
          return b.amount - a.amount;
      }
    });
    return arr;
  });

  // Top N
  maxRows = computed(() => this.filtered().length);
  private top = computed(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    return this.sorted().slice(0, n);
  });

  // ===== Totaux & « Autres » =====
  private totalAll = computed(() => {
    const mode = this.viewMode();
    return this.filtered().reduce(
      (a, x) => a + (mode === "CA" ? x.amount : x.qty),
      0,
    );
  });
  private totalTopOnly = computed(() => {
    const mode = this.viewMode();
    return this.top().reduce(
      (a, x) => a + (mode === "CA" ? x.amount : x.qty),
      0,
    );
  });
  private othersValue = computed(() =>
    Math.max(0, this.totalAll() - this.totalTopOnly()),
  );
  private hasOthers = computed(
    () =>
      this.filtered().length >
      Math.max(1, Math.min(this.topCount(), this.maxRows())),
  );

  // Données (PIE + LIST)
  private pieLabels = computed(() => {
    const labs = this.top().map((x) => x.label);
    if (this.hasOthers()) labs.push("Autres");
    return labs;
  });

  // ⚠️ public pour empty state dans le template
  values = computed(() => {
    const vals = this.top().map((x) =>
      this.viewMode() === "CA" ? x.amount : x.qty,
    );
    if (this.hasOthers()) vals.push(this.othersValue());
    return vals;
  });

  // Totaux pour affichage LIST
  private total = computed(() => this.values().reduce((a, b) => a + b, 0)); // = totalAll (inclut « Autres »)
  totalTop = () => this.totalTopOnly();
  totalTopPct = () => {
    const all = this.totalAll();
    return all > 0 ? (this.totalTopOnly() / all) * 100 : 0;
  };

  listRows = computed(() => {
    const totAll = this.total() || 0; // total global (inclut « Autres »)
    const mode = this.viewMode();
    return this.top().map((x) => {
      const value = mode === "CA" ? x.amount : x.qty;
      const pct = totAll > 0 ? (value / totAll) * 100 : 0;
      return { key: x.key, label: x.label, value, pct };
    });
  });

  // ===== Palette dynamique =====
  private makeColors(n: number): string[] {
    const base = [140, 210, 260, 275, 40, 0, 195, 220];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 85% 55%)`);
    }
    return out;
  }

  private colors = computed(() => this.makeColors(this.values().length));

  // ===== Helpers =====
  private arrEq<T>(a?: T[] | null, b?: T[] | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  constructor() {
    // Clamp du slider (ne pas écraser la valeur utilisateur)
    effect(
      () => {
        // 1) Reset sur changement de stats
        this.stats(); // dépendance voulue
        const max = untracked(() => this.maxRows()) || 1; // ne pas déclencher sur maxRows
        this.topCount.set(Math.min(10, max));
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        // 2) Clamp quand maxRows évolue
        const max = this.maxRows() || 1;
        this.topCount.update((v) => Math.min(Math.max(1, v || 1), max));
      },
      { allowSignalWrites: true },
    );

    // MAJ chart live + légende externe
    effect(
      () => {
        if (this.viewType() !== "PIE") return;
        const canvas = this.canvasRef();
        if (!canvas || !this.chart) return;

        const labels = this.pieLabels();
        const values = this.values();
        const ds = this.chart.data.datasets[0] as ChartDataset<
          "doughnut",
          number[]
        >;
        const newLabel = this.viewMode() === "CA" ? "CA" : "Unités";

        const prevLabels = (this.chart.data.labels ?? []) as string[];
        const prevValues = (ds.data ?? []) as number[];

        this.chart.data.labels = labels;
        ds.data = values;
        ds.label = newLabel;

        if (!this.arrEq(values, prevValues)) {
          ds.backgroundColor = this.colors();
        }

        if (
          !this.arrEq(labels, prevLabels) ||
          !this.arrEq(values, prevValues)
        ) {
          this.chart.update(); // trigger generateLabels -> maj legendLabels
        }
      },
      { allowSignalWrites: true },
    );

    // (Re)build chart quand données + canvas dispo + vue PIE
    effect(
      () => {
        if (this.viewType() !== "PIE") {
          this.destroyChart(true);
          return;
        }
        const canvas = this.canvasRef();
        if (!canvas) {
          this.destroyChart(true);
          return;
        }
        if (!this.values().length) {
          this.destroyChart(true);
          return;
        }
        this.buildChart();
      },
      { allowSignalWrites: true },
    );
  }

  private destroyChart(clearLegend = false) {
    if (clearLegend) this.legendLabels.set([]);
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart() {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    this.destroyChart(true);

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels: this.pieLabels(),
        datasets: [
          {
            label: this.viewMode() === "CA" ? "CA" : "Unités",
            data: this.values(),
            backgroundColor: this.colors(),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 120,
        plugins: {
          legend: {
            display: true, // on garde pour déclencher generateLabels
            position: "right",
            labels: {
              usePointStyle: true,
              generateLabels: (c) => {
                const data = c.data as any;
                const labels = (data.labels || []) as string[];
                const values = (data.datasets?.[0]?.data || []) as number[];
                const colors = (data.datasets?.[0]?.backgroundColor ||
                  []) as string[];
                const total = values.reduce((a, b) => +a + +b, 0);
                const out = labels.map((lbl, i) => {
                  const val = +values[i] || 0;
                  const pct =
                    total > 0 ? ((val / total) * 100).toFixed(0) : "0";
                  return {
                    label: lbl,
                    percentage: pct,
                    color: String(colors[i] ?? "#999"),
                  };
                });
                this.legendLabels.set(out);
                return []; // masque la légende native
              },
            },
          },
          tooltip: {
            bodyFont: { size: 12 },
            titleFont: { size: 12 },
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed as number;
                const isCA = this.viewMode() === "CA";
                return ` ${ctx.label}: ${isCA ? this.fmtEUR.format(val) : this.fmtINT.format(val)}`;
              },
            },
          },
          title: { display: false },
        },
        layout: { padding: { top: 6, right: 6, bottom: 6, left: 6 } },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }

  formatValue(v: number): string {
    return this.viewMode() === "CA"
      ? this.fmtEUR.format(v)
      : this.fmtINT.format(v);
  }

  protected readonly Math = Math;
}
